import { useLayoutEffect } from 'react'
import { Controller, useFormContext, type ControllerProps } from 'react-hook-form'
import MenuItem from '@mui/material/MenuItem'
import TextField, { type TextFieldProps } from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import GridItem, { type GridItemProps, omitGridProps } from './GridItem'
import Tooltip from './Tooltip'

export interface RichOption {
    name: string
    value?: string | number
    disabled?: boolean
    tooltip?: string | React.ReactNode
    hoverTooltip?: string
}
export type Option = RichOption | string | number

const getValue = (o: Option): string | number => {
    return typeof o !== 'object' ? o : (o.value ?? o.name)
}

const customPropKeys = ['defaultFirst', 'onChange', 'options', 'label', 'rules', 'helperText'] as const

const omitCustomProps = <T extends Record<string, unknown>>(obj: T) => {
    const result = { ...obj }
    for (const key of customPropKeys) {
        delete result[key]
    }
    return result
}

export interface SelectProps extends GridItemProps {
    name: ControllerProps['name']
    label?: string
    options: Array<Option>
    defaultFirst?: boolean
    required?: boolean
    disabled?: boolean
    rules?: ControllerProps['rules']
    variant?: TextFieldProps['variant']
    hiddenLabel?: boolean
    helperText?: React.ReactNode
    slotProps?: TextFieldProps['slotProps']
    onChange?: (value: string | number) => void
}

/**
 * Simple select component using React Hook Form
 * @param props {
 *   name: field name
 *   label?: label to show
 *   required?: adds validation
 *   options: array of string, numbers or complex objects with name, value, disabled state and tooltip
 *   defaultFirst?: If true, options[0] is set by default
 *   onChange?: callback function to handle selection changes
 * }
 */
const Select = (props: SelectProps) => {
    const { rules = {} } = props
    if (props.required && !rules.required && !rules.validate) {
        rules.required = 'This field is required'
    }

    const { getValues, setValue } = useFormContext()
    useLayoutEffect(() => {
        if (!props.defaultFirst || props.options.length === 0) return

        const current = getValues(props.name)
        if ([undefined, ''].includes(current)) {
            setValue(props.name, getValue(props.options[0]), { shouldDirty: true })
        }
    }, [])

    const hasEmpty = !!props.options.find((option) => typeof option === 'object' && option.value === '')

    return (
        <GridItem {...props}>
            <Controller
                name={props.name}
                rules={rules}
                render={({ field: { ref, value, onChange, ...field }, fieldState }) => {
                    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
                        const selectedValue = event.target.value as string | number
                        onChange(selectedValue)
                        props.onChange?.(selectedValue)
                    }

                    const showError = fieldState.invalid
                    const fieldError = fieldState.error?.message ?? ''

                    return (
                        <TextField
                            error={showError}
                            helperText={showError ? fieldError : props.helperText}
                            {...omitCustomProps(omitGridProps(props as unknown as Record<string, unknown>))}
                            {...field}
                            inputRef={ref}
                            value={value ?? ''}
                            onChange={handleChange}
                            select
                            label={props.hiddenLabel ? undefined : props.label}
                            margin="normal"
                            fullWidth
                            variant={props.variant ?? 'outlined'}
                            hiddenLabel={props.hiddenLabel}
                            data-test-id={`select-${props.name}`}
                            defaultValue=""
                            slotProps={{
                                ...props.slotProps,
                                select: { displayEmpty: hasEmpty },
                                inputLabel: { shrink: hasEmpty || undefined },
                            }}
                        >
                            {props.options.map((option) => {
                                const opt = typeof option !== 'object' ? ({ name: option } as RichOption) : option
                                const tooltipStyle = opt.hoverTooltip ? 'hover' : opt.tooltip ? 'inline' : 'none'
                                // A disabled MenuItem has pointer-events: none, which suppresses its
                                // hover tooltip. Re-enable pointer events on the label so the tooltip
                                // still shows, and swallow the click so the option stays unselectable.
                                const reenableTooltipOnDisabled = Boolean(opt.disabled) && tooltipStyle === 'hover'
                                const body = (
                                    <span
                                        onClick={reenableTooltipOnDisabled ? (e) => e.stopPropagation() : undefined}
                                        style={{
                                            fontStyle: 'value' in opt && opt.value === undefined ? 'italic' : undefined,
                                            marginRight: 5,
                                            ...(tooltipStyle === 'hover' ? { width: '100%' } : undefined),
                                            ...(reenableTooltipOnDisabled ? { pointerEvents: 'auto' } : undefined),
                                        }}
                                    >
                                        {opt.name}
                                    </span>
                                )
                                return (
                                    <MenuItem value={getValue(opt)} key={opt.name} disabled={Boolean(opt.disabled)}>
                                        {tooltipStyle === 'hover' && (
                                            // Portal to the body so the tooltip floats above the open
                                            // menu instead of rendering behind the sibling options.
                                            <Tooltip title={opt.hoverTooltip} disablePortal={false}>
                                                {body}
                                            </Tooltip>
                                        )}
                                        {tooltipStyle !== 'hover' && body}
                                        {tooltipStyle === 'inline' && typeof opt.tooltip === 'string' && (
                                            <Typography variant="body2" component="span">
                                                &nbsp;({opt.tooltip})
                                            </Typography>
                                        )}
                                        {tooltipStyle === 'inline' && typeof opt.tooltip === 'object' && opt.tooltip}
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    )
                }}
            />
        </GridItem>
    )
}

export default Select
